/**
 * Базовый класс для всех API ошибок
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: unknown,
    public originalError?: Error
  ) {
    super(message)
    this.name = 'ApiError'
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

/**
 * Ошибка сети (нет соединения, timeout)
 */
export class NetworkError extends ApiError {
  constructor(message: string = 'Network error occurred', originalError?: Error) {
    super(message, undefined, undefined, originalError)
    this.name = 'NetworkError'
    Object.setPrototypeOf(this, NetworkError.prototype)
  }
}

/**
 * Ошибка валидации (400)
 */
export class ValidationError extends ApiError {
  constructor(
    message: string = 'Validation error',
    public validationErrors?: Record<string, string[]>,
    response?: unknown
  ) {
    super(message, 400, response)
    this.name = 'ValidationError'
    Object.setPrototypeOf(this, ValidationError.prototype)
  }
}

/**
 * Ошибка авторизации (401)
 */
export class UnauthorizedError extends ApiError {
  constructor(message: string = 'Unauthorized', response?: unknown) {
    super(message, 401, response)
    this.name = 'UnauthorizedError'
    Object.setPrototypeOf(this, UnauthorizedError.prototype)
  }
}

/**
 * Ошибка доступа (403)
 */
export class ForbiddenError extends ApiError {
  constructor(message: string = 'Forbidden', response?: unknown) {
    super(message, 403, response)
    this.name = 'ForbiddenError'
    Object.setPrototypeOf(this, ForbiddenError.prototype)
  }
}

/**
 * Ошибка не найдено (404)
 */
export class NotFoundError extends ApiError {
  constructor(message: string = 'Not found', response?: unknown) {
    super(message, 404, response)
    this.name = 'NotFoundError'
    Object.setPrototypeOf(this, NotFoundError.prototype)
  }
}

/**
 * Превышен лимит запросов (429)
 */
export class RateLimitError extends ApiError {
  constructor(message: string = 'Too many requests', response?: unknown) {
    super(message, 429, response)
    this.name = 'RateLimitError'
    Object.setPrototypeOf(this, RateLimitError.prototype)
  }
}

/**
 * Ошибка сервера (500+)
 */
export class ServerError extends ApiError {
  constructor(message: string = 'Server error', statusCode: number = 500, response?: unknown) {
    super(message, statusCode, response)
    this.name = 'ServerError'
    Object.setPrototypeOf(this, ServerError.prototype)
  }
}

/**
 * Ошибка отмены запроса (AbortController)
 */
export class AbortError extends ApiError {
  constructor(message: string = 'Request aborted') {
    super(message, undefined, undefined)
    this.name = 'AbortError'
    Object.setPrototypeOf(this, AbortError.prototype)
  }
}

/**
 * Типы ошибок для type guards
 */
export type ApiErrorType =
  | ApiError
  | NetworkError
  | ValidationError
  | UnauthorizedError
  | ForbiddenError
  | NotFoundError
  | RateLimitError
  | ServerError
  | AbortError

